/**
 * Driving a dim: what has to move for a dimension to read the value someone
 * typed. Pure, so the rule is one function rather than call-site vigilance;
 * the editor side reads the tree, calls `planDrive`, and applies the moves.
 *
 * One rule decides everything. A dim measures between two references, so
 * driving it moves the `end` reference and leaves the `start` reference as
 * the datum. Inside a chain the same move carries every link downstream of
 * the driven one, which is what keeps their authored values intact and lets
 * the overall dimension grow (a string dim's standard behavior). Everything
 * the chain references and does not move is a fixed point, and that is what
 * decides how a shape moves: a shape with nothing fixed on it translates, a
 * shape with a fixed reference on it scales about that reference, and
 * references that pin exact polyline vertices move those vertices alone.
 */
import type { PagePoint } from "./geometry";
import type { DimDirection, DimTerminal } from "./shapes/dim";

const EPSILON = 1e-6;
const MIN_SPAN = 1e-6;

/** One end of one dim, and what it is pinned to. */
export interface DriveRef {
  dim: string;
  terminal: DimTerminal;
  /** The shape it is pinned to, or null when it is the dim's own free point. */
  shape: string | null;
  /** The exact polyline vertices it pins: one for a point, two for an edge. */
  vertices: readonly string[];
  /** Current page position. */
  at: PagePoint;
}

export interface DriveLink {
  start: DriveRef;
  end: DriveRef;
}

export type DriveMove =
  | {
      kind: "point";
      dim: string;
      terminal: DimTerminal;
      dx: number;
      dy: number;
    }
  | { kind: "vertex"; shape: string; vertex: string; dx: number; dy: number }
  | { kind: "translate"; shape: string; dx: number; dy: number }
  | { kind: "scale"; shape: string; scale: PagePoint; origin: PagePoint };

/** How far the `end` reference has to move for the dim to read `target`. */
export function driveDelta(
  link: DriveLink,
  direction: DimDirection,
  target: number,
): PagePoint {
  const { at: a } = link.start;
  const { at: b } = link.end;
  if (direction === "aligned") {
    const span = { x: b.x - a.x, y: b.y - a.y };
    const length = Math.hypot(span.x, span.y) || 1;
    const k = (target - length) / length;
    return { x: span.x * k, y: span.y * k };
  }
  const axis = direction === "x" ? "x" : "y";
  const current = b[axis] - a[axis];
  const grown = Math.sign(current || 1) * target - current;
  return { x: axis === "x" ? grown : 0, y: axis === "y" ? grown : 0 };
}

/**
 * The moves that make `link` measure `target`. `siblings` are the other links
 * of the same chain (empty for a lone dim); the ones downstream of this link
 * ride along, and every other reference in the chain stays put.
 */
export function planDrive(
  link: DriveLink,
  siblings: readonly DriveLink[],
  direction: DimDirection,
  target: number,
): DriveMove[] {
  const delta = driveDelta(link, direction, target);
  if (Math.hypot(delta.x, delta.y) < EPSILON) return [];

  const downstream = downstreamOf(link, siblings, direction);
  const movers = [link.end, ...downstream.flatMap((l) => [l.start, l.end])];
  const fixed = [
    link.start,
    ...notIn(siblings, downstream).flatMap((l) => [l.start, l.end]),
  ];

  const moves: DriveMove[] = [];
  for (const ref of movers) {
    if (ref.shape === null) {
      moves.push({
        kind: "point",
        dim: ref.dim,
        terminal: ref.terminal,
        dx: delta.x,
        dy: delta.y,
      });
    }
  }
  for (const [shape, refs] of byShape(movers, fixed)) {
    moves.push(...shapeMoves(shape, refs, direction, delta));
  }
  return moves;
}

/**
 * The links a move carries: those that start at or beyond where this dim
 * ends, measured along the way this dim points.
 */
function downstreamOf(
  link: DriveLink,
  siblings: readonly DriveLink[],
  direction: DimDirection,
): DriveLink[] {
  const along = alongRun(link, direction);
  const here = along(link.end.at);
  return siblings.filter((s) => along(s.start.at) >= here - EPSILON);
}

/**
 * How far along a string a page point sits. The axis is the dim's own
 * direction, which is authored, and only the way it runs is read off its
 * ends: an east-west string measures east-west however high or low its
 * references were picked, and a string drawn right to left is still ordered
 * from its own first bay rather than from the page's left edge.
 */
function alongRun(
  link: DriveLink,
  direction: DimDirection,
): (p: PagePoint) => number {
  if (direction === "aligned") {
    const span = {
      x: link.end.at.x - link.start.at.x,
      y: link.end.at.y - link.start.at.y,
    };
    const length = Math.hypot(span.x, span.y) || 1;
    return (p) => (p.x * span.x + p.y * span.y) / length;
  }
  const axis = direction === "x" ? "x" : "y";
  const way = Math.sign(link.end.at[axis] - link.start.at[axis]) || 1;
  return (p) => p[axis] * way;
}

function notIn(
  links: readonly DriveLink[],
  exclude: readonly DriveLink[],
): DriveLink[] {
  return links.filter((link) => !exclude.includes(link));
}

interface ShapeRefs {
  movers: DriveRef[];
  fixed: DriveRef[];
}

function byShape(
  movers: readonly DriveRef[],
  fixed: readonly DriveRef[],
): Map<string, ShapeRefs> {
  const out = new Map<string, ShapeRefs>();
  const bucket = (shape: string) => {
    const existing = out.get(shape);
    if (existing) return existing;
    const fresh: ShapeRefs = { movers: [], fixed: [] };
    out.set(shape, fresh);
    return fresh;
  };
  for (const ref of movers) if (ref.shape) bucket(ref.shape).movers.push(ref);
  for (const ref of fixed) if (ref.shape) bucket(ref.shape).fixed.push(ref);
  return new Map([...out].filter(([, refs]) => refs.movers.length > 0));
}

/** How one shape moves, given which of its references move and which stay. */
function shapeMoves(
  shape: string,
  refs: ShapeRefs,
  direction: DimDirection,
  delta: PagePoint,
): DriveMove[] {
  // Every reference on this shape pins exact vertices, so move exactly those:
  // a bay of a wall run moves without dragging the walls it meets.
  if ([...refs.movers, ...refs.fixed].every((ref) => ref.vertices.length)) {
    const vertices = new Set(refs.movers.flatMap((ref) => ref.vertices));
    return [...vertices].map((vertex) => ({
      kind: "vertex" as const,
      shape,
      vertex,
      dx: delta.x,
      dy: delta.y,
    }));
  }
  // Nothing on this shape has to stay put, so it moves whole.
  if (!refs.fixed.length) {
    return [{ kind: "translate", shape, dx: delta.x, dy: delta.y }];
  }
  // Something on it does, so it stretches about that: the datum end of a
  // dimensioned rectangle stays where it is while the far end moves.
  const origin = refs.fixed[0]!.at;
  const moved = farthestFrom(origin, refs.movers, direction);
  return [
    {
      kind: "scale",
      shape,
      origin,
      scale: scaleFor(origin, moved, delta, direction),
    },
  ];
}

function farthestFrom(
  origin: PagePoint,
  refs: readonly DriveRef[],
  direction: DimDirection,
): PagePoint {
  const reach = (p: PagePoint) =>
    direction === "x"
      ? Math.abs(p.x - origin.x)
      : direction === "y"
        ? Math.abs(p.y - origin.y)
        : Math.hypot(p.x - origin.x, p.y - origin.y);
  return refs.reduce((best, ref) =>
    reach(ref.at) > reach(best.at) ? ref : best,
  ).at;
}

/** The scale that lands `moved` at `moved + delta` about `origin`. */
function scaleFor(
  origin: PagePoint,
  moved: PagePoint,
  delta: PagePoint,
  direction: DimDirection,
): PagePoint {
  if (direction === "x") {
    const span = moved.x - origin.x;
    return { x: (span + delta.x) / signedSpan(span), y: 1 };
  }
  if (direction === "y") {
    const span = moved.y - origin.y;
    return { x: 1, y: (span + delta.y) / signedSpan(span) };
  }
  const span = Math.hypot(moved.x - origin.x, moved.y - origin.y);
  const grown = Math.hypot(
    moved.x + delta.x - origin.x,
    moved.y + delta.y - origin.y,
  );
  const k = grown / signedSpan(span);
  return { x: k, y: k };
}

function signedSpan(span: number): number {
  return Math.abs(span) < MIN_SPAN ? Math.sign(span || 1) * MIN_SPAN : span;
}
